// import { Currency, Price } from '@uniswap/sdk-core'
import Loader from './LoadingSpinner'
import { Chart } from './Chart'
import { useDensityChartData } from './hooks'
import { Bound, ZoomLevels } from './types'
import styled, { useTheme } from 'styled-components'
import { saturate } from 'polished'
import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react'
import { BarChart2, CloudOff, Inbox } from 'react-feather'
import { PhantomTypeArgument, StructClass } from '@/shared/gen/_framework/reified'
import { Price, ClmmPool, tickIndexToSqrtPriceX64 } from '@kunalabs-io/kai'
import { Typography } from 'components/Typography/Typography'
import { AutoColumn, ColumnCenter } from './Column'
import Decimal from 'decimal.js'

// Used to format floats representing percent change with fixed decimal places
function formatDelta(delta: number | null | undefined) {
  if (delta === null || delta === undefined || delta === Infinity || isNaN(delta)) {
    return '-'
  }

  return `${Number(Math.abs(delta).toFixed(2)).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    useGrouping: false,
  })}%`
}

enum FeeAmount {
  VERY_LOW = 400, // 0-400
  LOW = 1500, // 401-1500
  MEDIUM = 5000, // 1501-5000
  HIGH = 10000, // 5001+
}

const LOW_ZOOM_LEVEL: ZoomLevels = {
  initialMin: 0.99925,
  initialMax: 1.00075,
  min: 0.00001,
  max: 1.5,
  initialTickWidthMultiplier: 1,
}

const MEDIUM_ZOOM_LEVEL: ZoomLevels = {
  initialMin: 0.7,
  initialMax: 1.3,
  min: 0.00001,
  max: 20,
}

const HIGH_ZOOM_LEVEL: ZoomLevels = {
  initialMin: 0.5,
  initialMax: 2,
  min: 0.00001,
  max: 20,
}

const ZOOM_LEVELS: Record<FeeAmount, ZoomLevels> = {
  [FeeAmount.VERY_LOW]: LOW_ZOOM_LEVEL,
  [FeeAmount.LOW]: LOW_ZOOM_LEVEL,
  [FeeAmount.MEDIUM]: MEDIUM_ZOOM_LEVEL,
  [FeeAmount.HIGH]: HIGH_ZOOM_LEVEL,
}

function getFeeAmountTier(feeRate: number): FeeAmount {
  if (feeRate <= 400) return FeeAmount.VERY_LOW
  if (feeRate <= 1500) return FeeAmount.LOW
  if (feeRate <= 5000) return FeeAmount.MEDIUM
  return FeeAmount.HIGH
}

function getZoomLevels(pool: ClmmPool<StructClass, unknown, PhantomTypeArgument, PhantomTypeArgument>) {
  const feeAmount = getFeeAmountTier(Number(pool.feeRate()))
  const ret = ZOOM_LEVELS[feeAmount]
  return ret
}

function getInitialDomain(
  pool: ClmmPool<StructClass, unknown, PhantomTypeArgument, PhantomTypeArgument>,
  reverseTokens: boolean
): [number, number] | undefined {
  const zoomLevels = getZoomLevels(pool)
  if (!zoomLevels.initialTickWidthMultiplier) {
    return undefined
  }

  const pa = pool.tickToPrice(pool.currentTick())
  const pb = pool.tickToPrice(pool.currentTick() + pool.tickSpacing * zoomLevels.initialTickWidthMultiplier)

  return reverseTokens
    ? [pb.inverted().human.toNumber(), pa.inverted().human.toNumber()]
    : [pa.human.toNumber(), pb.human.toNumber()]
}

const ChartWrapper = styled.div<{ $interactive: boolean }>`
  position: relative;
  width: 100%;
  max-height: 200px;
  justify-content: center;
  align-content: center;

  ${p =>
    !p.$interactive &&
    `
     rect {
      pointer-events: none;
     }
    `}
`

function InfoBox({ message, icon }: { message?: ReactNode; icon: ReactNode }) {
  return (
    <ColumnCenter style={{ height: '100%', justifyContent: 'center' }}>
      {icon}
      {message && <Typography style={{ padding: 10, marginTop: '20px', textAlign: 'center' }}>{message}</Typography>}
    </ColumnCenter>
  )
}

export default function LiquidityChartRangeInput({
  pool,
  reverseTokens,
  ticksAtLimit,
  tickA,
  tickB,
  onTickAChange,
  onTickBChange,
  interactive,
}: {
  pool: ClmmPool<StructClass, unknown, PhantomTypeArgument, PhantomTypeArgument>
  reverseTokens: boolean
  ticksAtLimit: { [bound in Bound]?: boolean | undefined }
  tickA?: number
  tickB?: number
  onTickAChange: (tickA: number) => void
  onTickBChange: (tickB: number) => void
  interactive: boolean
}) {
  const theme = useTheme()

  const tokenAColor = theme.colors.rangePicker.accent1
  const tokenBColor = theme.colors.rangePicker.accent1

  const { isLoading, error, data } = useDensityChartData(pool)
  const chartData = useMemo(() => {
    if (!data) {
      return undefined
    }
    if (reverseTokens) {
      return data.map(d => ({ ...d, price0: 1 / d.price0 }))
    } else {
      return data
    }
  }, [data, reverseTokens])

  const price = reverseTokens ? pool.currentPrice().inverted().human.toNumber() : pool.currentPrice().human.toNumber()

  const [brushDomain, setBrushDomain] = useState<[number, number] | undefined>(undefined)

  const onBrushDomainChangeEnded = useCallback(
    (domain: [number, number], mode: string | undefined) => {
      let leftRangeValue = Number(domain[0])
      const rightRangeValue = Number(domain[1])

      if (leftRangeValue <= 0) {
        leftRangeValue = 1 / 10 ** 6
      }

      const [leftPrice, leftTick] = pool.priceToClosestInitializablePrice(
        reverseTokens
          ? Price.fromHuman(pool.Y, pool.X, leftRangeValue).inverted()
          : Price.fromHuman(pool.X, pool.Y, leftRangeValue)
      )
      const [rightPrice, rightTick] = pool.priceToClosestInitializablePrice(
        reverseTokens
          ? Price.fromHuman(pool.Y, pool.X, rightRangeValue).inverted()
          : Price.fromHuman(pool.X, pool.Y, rightRangeValue)
      )

      if (reverseTokens) {
        setBrushDomain([
          leftPrice.inverted().human.toSignificantDigits(6).toNumber(),
          rightPrice.inverted().human.toSignificantDigits(6).toNumber(),
        ])
      } else {
        setBrushDomain([
          leftPrice.human.toSignificantDigits(6).toNumber(),
          rightPrice.human.toSignificantDigits(6).toNumber(),
        ])
      }

      // simulate user input for auto-formatting and other validations
      if (
        (!ticksAtLimit[reverseTokens ? Bound.LOWER : Bound.UPPER] || mode === 'handle' || mode === 'reset') &&
        leftRangeValue > 0
      ) {
        if (reverseTokens) {
          onTickBChange(leftTick)
        } else {
          onTickAChange(leftTick)
        }
      }

      if ((!ticksAtLimit[reverseTokens ? Bound.UPPER : Bound.LOWER] || mode === 'reset') && rightRangeValue > 0) {
        // todo: remove this check. Upper bound for large numbers
        if (rightRangeValue < 1e35) {
          if (reverseTokens) {
            onTickAChange(rightTick)
          } else {
            onTickBChange(rightTick)
          }
        }
      }
    },
    [reverseTokens, onTickAChange, onTickBChange, ticksAtLimit, pool]
  )

  interactive = interactive && Boolean(chartData?.length)

  useEffect(() => {
    if (!pool || tickA === undefined || tickB === undefined) {
      setBrushDomain(undefined)
      return
    }

    const leftTick = reverseTokens ? tickB : tickA
    const rightTick = reverseTokens ? tickA : tickB

    const tickToPrice = (tick: number) => {
      const sqrtPriceX64 = tickIndexToSqrtPriceX64(tick)
      const price = new Decimal((sqrtPriceX64 * sqrtPriceX64).toString()).div((1n << 128n).toString())
      return Price.fromNumeric(pool.X, pool.Y, price)
    }

    let leftPrice = tickToPrice(leftTick)
    let rightPrice = tickToPrice(rightTick)
    if (reverseTokens) {
      leftPrice = leftPrice.inverted()
      rightPrice = rightPrice.inverted()
    }

    setBrushDomain([
      leftPrice.human.toSignificantDigits(6).toNumber(),
      rightPrice.human.toSignificantDigits(6).toNumber(),
    ])
  }, [reverseTokens, tickA, tickB, pool])

  const brushLabelValue = useCallback(
    (d: 'w' | 'e', x: number) => {
      if (d === 'w' && ticksAtLimit[reverseTokens ? Bound.LOWER : Bound.UPPER]) {
        return '0'
      }
      if (d === 'e' && ticksAtLimit[reverseTokens ? Bound.UPPER : Bound.LOWER]) {
        return '∞'
      }

      const percent = (x < price ? -1 : 1) * ((Math.max(x, price) - Math.min(x, price)) / price) * 100

      return `${(Math.sign(percent) < 0 ? '-' : '') + formatDelta(percent)}`
    },
    [reverseTokens, price, ticksAtLimit]
  )

  const isUninitialized = !pool || (chartData === undefined && !isLoading)

  return (
    <AutoColumn gap="md" style={{ minHeight: '200px' }}>
      {isUninitialized ? (
        <InfoBox
          message="Your position will appear here."
          icon={<Inbox size={56} stroke={theme.colors.rangePicker.neutral1} />}
        />
      ) : isLoading ? (
        <InfoBox icon={<Loader size="40px" stroke={theme.colors.rangePicker.neutral2} />} />
      ) : error ? (
        <InfoBox
          message="Liquidity data not available."
          icon={<CloudOff size={56} stroke={theme.colors.rangePicker.neutral2} />}
        />
      ) : !chartData || chartData.length === 0 || !price ? (
        <InfoBox
          message="There is no liquidity data"
          icon={<BarChart2 size={56} stroke={theme.colors.rangePicker.neutral2} />}
        />
      ) : (
        <ChartWrapper $interactive={interactive}>
          <Chart
            data={{
              series: chartData,
              current: price,
            }}
            dimensions={{ width: 800, height: 200 }}
            margins={{ top: 10, right: 2, bottom: 20, left: 0 }}
            styles={{
              area: {
                selection: theme.colors.rangePicker.accent1,
              },
              brush: {
                handle: {
                  west: saturate(0.1, tokenAColor) ?? theme.colors.rangePicker.critical,
                  east: saturate(0.1, tokenBColor) ?? theme.colors.rangePicker.accent1,
                },
              },
            }}
            interactive={interactive}
            brushLabels={brushLabelValue}
            brushDomain={brushDomain}
            onBrushDomainChange={onBrushDomainChangeEnded}
            zoomLevels={getZoomLevels(pool)}
            ticksAtLimit={ticksAtLimit}
            initialDomain={getInitialDomain(pool, reverseTokens)}
          />
        </ChartWrapper>
      )}
    </AutoColumn>
  )
}
